import { ref, computed } from "vue";
import { nanoid } from "nanoid";
import type { LabwareObject } from "@/automation/labware/labwareManager";
import { assignLabwareToDeck } from "@/automation/utils/deckLayoutUtils";
import type { SystemConfig } from "@/state/configState";
import type { ProtocolExport } from "@/automation/protocolExport";

// Define interfaces (same as in runState)
export interface DeckPosition {
  id: string;
  allowedLabware?: string[];
  fixedLabware?: string; // type-id of permanently installed labware
  currentLabware?: LabwareObject;
  isVerified?: boolean;
  /**
   * Underlying position ids this "spanning" position physically covers
   * (e.g. Position 13 / "A13" covers ["A1","A4"]). When this position holds
   * labware, the covered positions are blocked and the group renders as one
   * merged nest. Absent for normal positions.
   */
  coversPositions?: string[];
}

export interface DeckConfiguration {
  id: string;
  label: string;
  positions: DeckPosition[];
  sections: {
    id: string;
    label: string;
    positionIds: string[];
    gridCols?: number;  // Number of columns for grid rendering
    deckArea?: string;  // 'upper' | 'lower' | 'main' | 'side' - for heuristics
    orientation?: string;  // 'vertical' | 'horizontal' - for section layout direction
  }[];
}

// --- Module-level (singleton) state ---
const deckConfiguration = ref<DeckConfiguration | null>(null);
const unassignedLabware = ref<LabwareObject[]>([]);
const isInitialized = ref(false);
const isLoadingLayout = ref(false);
const forceIncludeFixedLabware = ref(true); // Default ON for backward compatibility
const hasUnreferencedFixedLabware = ref(false); // Track if there's unreferenced fixed labware
// Free placement ("expert") mode — when ON, the user drags labware by hand and the
// solver is bypassed entirely. All edits below mutate the resolved layout in-memory.
const freeMode = ref(false);
// Whether the user has actually moved anything since entering free mode — gates the
// "you'll lose your layout" warning when they flip back to validated mode.
const freeModeDirty = ref(false);

// --- Computed properties ---
// Determine if the deck is ready (all required positions verified)
const isReady = computed<boolean>(() => {
  if (!deckConfiguration.value) return false;

  if (isLoadingLayout.value) return false;

  if (unassignedLabware.value.length > 0) return false;

  return deckConfiguration.value.positions.every(
    (position: DeckPosition) => !position.currentLabware || position.isVerified
  );
});

// Helper function to extract referenced labware IDs from protocol steps
export function getReferencedLabwareIds(protocol: ProtocolExport): Set<string> {
  const referencedIds = new Set<string>();

  // Process all steps in the protocol
  for (const step of protocol.steps) {
    // Look at each parameter in the step
    for (const param of step.parameters) {
      // Check if parameter value is a labware reference
      if (param.value && typeof param.value === "object") {
        if (Array.isArray(param.value)) {
          // Handle array of labware values
          for (const item of param.value) {
            if (
              item &&
              typeof item === "object" &&
              "type" in item &&
              item.type === "labware" &&
              item.labware &&
              item.labware.id
            ) {
              referencedIds.add(item.labware.id);
            }
          }
        } else if (
          "type" in param.value &&
          param.value.type === "labware" &&
          param.value.labware &&
          param.value.labware.id
        ) {
          // Handle single labware value
          referencedIds.add(param.value.labware.id);
        }
      }
    }
  }

  return referencedIds;
}

/**
 * Map labware instance id -> forced deck position id, derived from any step
 * param carrying a `deckPositionOverride` in the active config (e.g. a SELECT
 * block pinning its source plate to "A13", the Position 13 nest). These become
 * fixed assignments so the plate always lands on the spanning position.
 */
function computeDeckPositionOverrides(
  protocol: ProtocolExport,
  activeConfig: SystemConfig
): Record<string, string> {
  const overrides: Record<string, string> = {};
  const stepsById = new Map((activeConfig.steps ?? []).map((s) => [s.id, s]));
  for (const step of protocol.steps) {
    const cfgStep =
      (step.configId ? stepsById.get(step.configId) : undefined) ??
      stepsById.get(step.id);
    if (!cfgStep) continue;
    for (const cfgParam of cfgStep.params) {
      if (!cfgParam.deckPositionOverride) continue;
      const pParam = step.parameters.find((p) => p.id === cfgParam.id);
      const val = pParam?.value;
      const lwId =
        val && typeof val === "object" && !Array.isArray(val) && "labware" in val
          ? val.labware?.id
          : undefined;
      if (lwId) overrides[lwId] = cfgParam.deckPositionOverride;
    }
  }
  return overrides;
}

/**
 * Initialize the deck layout with required labware from protocol
 * @param protocol The complete protocol export with steps and labware
 * @param activeConfig System configuration that contains deck layouts
 */
async function initializeDeckLayout(
  protocol: ProtocolExport,
  activeConfig: SystemConfig
) {
  console.log("[DeckLayoutState] initializeDeckLayout called");
  isLoadingLayout.value = true; // Set loading to true
  isInitialized.value = false; // Reset initialized state at the beginning
  try {
    if (!activeConfig.robots || activeConfig.robots.length === 0) {
      console.error("No active robot found in configuration");
      // isInitialized remains false
      return;
    }

    // Get the active robot (first one for now)
    const activeRobot = activeConfig.robots[0];
    console.log("[DeckLayoutState] Active robot:", activeRobot);

    // Find the layout definition for the active robot
    const layoutId = activeRobot.deckLayoutId || "standard-dual-deck";
    console.log("[DeckLayoutState] Looking for layoutId:", layoutId);
    console.log("[DeckLayoutState] Available layouts:", activeConfig.deckLayouts?.map(l => l.id));

    const layout = activeConfig.deckLayouts?.find((l) => l.id === layoutId);

    if (!layout) {
      console.error(`No layout found for deck layout ID: ${layoutId}`);
      // isInitialized remains false
      return;
    }

    console.log("[DeckLayoutState] Found layout:", {
      id: layout.id,
      label: layout.label,
      positionCount: layout.positions?.length,
      sectionCount: layout.sections?.length,
      sections: layout.sections?.map(s => ({ id: s.id, positionIds: s.positionIds?.length }))
    });

    // Get only labware that is actually referenced in the protocol steps
    const referencedLabwareIds = getReferencedLabwareIds(protocol);

    // Build map of fixed labware type-ids to positions
    const fixedLabwareMap = new Map<
      string,
      { positionId: string; typeId: string }
    >();
    layout.positions.forEach((pos) => {
      if (pos.fixedLabware) {
        fixedLabwareMap.set(pos.fixedLabware, {
          positionId: pos.id,
          typeId: pos.fixedLabware,
        });
      }
    });

    // Determine which labware to include
    const labwareToInclude: LabwareObject[] = [];
    const fixedAssignments: Record<string, string> = {};
    const referencedFixedTypeIds = new Set<string>();

    // 1. Add referenced labware from protocol
    protocol.labware.forEach((labware) => {
      if (referencedLabwareIds.has(labware.id)) {
        labwareToInclude.push(labware);

        // If this labware type matches a fixed type, assign it to fixed position
        const fixedConfig = fixedLabwareMap.get(labware.typeId);
        if (fixedConfig) {
          fixedAssignments[labware.id] = fixedConfig.positionId;
          referencedFixedTypeIds.add(labware.typeId);
        }
      }
    });

    // 2. Determine if there are unreferenced fixed labware types
    const unreferencedFixedTypes = Array.from(fixedLabwareMap.keys()).filter(
      (typeId) => !referencedFixedTypeIds.has(typeId)
    );
    hasUnreferencedFixedLabware.value = unreferencedFixedTypes.length > 0;

    // 3. Add unreferenced fixed labware if force inclusion is ON
    if (forceIncludeFixedLabware.value) {
      fixedLabwareMap.forEach(({ positionId, typeId }) => {
        // Check if this type is already included
        const alreadyIncluded = labwareToInclude.some(
          (lw) => lw.typeId === typeId
        );

        if (!alreadyIncluded) {
          // Get labware type definition
          const labwareType = activeConfig.labware.find(
            (lw) => lw.id === typeId
          );
          if (labwareType) {
            // Create new instance for unreferenced fixed labware
            const fixedInstance: LabwareObject = {
              id: `fixed-${typeId}-${positionId}-${nanoid(6)}`,
              typeId: typeId,
              label: labwareType.label,
              isFixed: true,
            };

            labwareToInclude.push(fixedInstance);
            fixedAssignments[fixedInstance.id] = positionId;
          }
        }
      });
    }

    // Apply deck_position_override pins (e.g. a SELECT block's source → "A13").
    // These take precedence and become fixed assignments handled client-side by
    // assignLabwareToDeck (which also blocks the covered positions A1/A4).
    const overridePins = computeDeckPositionOverrides(protocol, activeConfig);
    for (const [lwId, posId] of Object.entries(overridePins)) {
      fixedAssignments[lwId] = posId;
    }

    // Create initial deck configuration from layout
    const initialConfig: DeckConfiguration = {
      id: layout.id,
      label: layout.label,
      positions: layout.positions.map((pos: any) => ({
        ...pos,
        isVerified: false,
      })),
      sections: layout.sections,
    };

    // Assign labware to the deck with fixed assignments
    const { updatedConfig, unassignedLabware: unassigned } =
      await assignLabwareToDeck(
        labwareToInclude,
        initialConfig,
        protocol,
        activeConfig,
        fixedAssignments // Pass fixed assignments
      );

    // Set state
    deckConfiguration.value = updatedConfig;
    unassignedLabware.value = unassigned;
    isInitialized.value = true; // Set to true only on successful initialization
  } catch (error) {
    console.error("Error initializing deck layout:", error);
    // isInitialized remains false due to the reset at the beginning or error
  } finally {
    isLoadingLayout.value = false; // Set loading to false when done
  }
}

/**
 * Update verification status for a deck position
 * @param positionId ID of the position to verify
 * @param isVerified New verification status
 */
function verifyPosition(positionId: string, isVerified: boolean) {
  if (!deckConfiguration.value) return;

  // Update the position in the deck configuration
  deckConfiguration.value.positions = deckConfiguration.value.positions.map(
    (position) => {
      if (position.id === positionId) {
        return { ...position, isVerified };
      }
      return position;
    }
  );
}

// --- Free placement mode mutators (solver bypassed; in-memory only) ---

/**
 * Swap or move a labware between two deck positions.
 * If the target is occupied, the two labware trade places (swap); if empty, it's a move.
 * Verification follows the labware (the user explicitly chose "verify unchanged on move").
 */
function moveBetweenPositions(fromPositionId: string, toPositionId: string) {
  if (!deckConfiguration.value || fromPositionId === toPositionId) return;

  const positions = deckConfiguration.value.positions;
  const from = positions.find((p) => p.id === fromPositionId);
  const to = positions.find((p) => p.id === toPositionId);
  if (!from?.currentLabware || !to) return;

  const movingLabware = from.currentLabware;
  const movingVerified = from.isVerified ?? false;
  const displacedLabware = to.currentLabware;
  const displacedVerified = to.isVerified ?? false;

  deckConfiguration.value.positions = positions.map((p) => {
    if (p.id === toPositionId) {
      return { ...p, currentLabware: movingLabware, isVerified: movingVerified };
    }
    if (p.id === fromPositionId) {
      return {
        ...p,
        currentLabware: displacedLabware,
        isVerified: displacedLabware ? displacedVerified : false,
      };
    }
    return p;
  });
  freeModeDirty.value = true;
}

/**
 * Place an unassigned (tray) labware onto a deck position.
 * If the position is occupied, its current occupant is pushed back to the tray (swap).
 */
function placeFromTray(labwareId: string, toPositionId: string) {
  if (!deckConfiguration.value) return;

  const labware = unassignedLabware.value.find((lw) => lw.id === labwareId);
  const to = deckConfiguration.value.positions.find(
    (p) => p.id === toPositionId
  );
  if (!labware || !to) return;

  const displaced = to.currentLabware;

  deckConfiguration.value.positions = deckConfiguration.value.positions.map(
    (p) =>
      p.id === toPositionId
        ? { ...p, currentLabware: labware, isVerified: false }
        : p
  );

  unassignedLabware.value = [
    ...unassignedLabware.value.filter((lw) => lw.id !== labwareId),
    ...(displaced ? [displaced] : []),
  ];
  freeModeDirty.value = true;
}

/**
 * Remove a labware from a deck position back into the unassigned tray.
 */
function unplaceToTray(fromPositionId: string) {
  if (!deckConfiguration.value) return;

  const from = deckConfiguration.value.positions.find(
    (p) => p.id === fromPositionId
  );
  if (!from?.currentLabware) return;

  const removed = from.currentLabware;

  deckConfiguration.value.positions = deckConfiguration.value.positions.map(
    (p) =>
      p.id === fromPositionId
        ? { ...p, currentLabware: undefined, isVerified: false }
        : p
  );
  unassignedLabware.value = [...unassignedLabware.value, removed];
  freeModeDirty.value = true;
}

/**
 * Reset the deck layout state
 */
function resetDeckLayoutState() {
  deckConfiguration.value = null;
  unassignedLabware.value = [];
  isInitialized.value = false;
  isLoadingLayout.value = false; // Reset loading state
  hasUnreferencedFixedLabware.value = false; // Reset unreferenced fixed labware flag
  freeMode.value = false; // Leave expert mode on close
  freeModeDirty.value = false;
}

// --- Composable Export ---
export function useDeckLayoutState() {
  return {
    deckConfiguration,
    unassignedLabware,
    isReady,
    isInitialized,
    isLoadingLayout, // Export new loading state
    forceIncludeFixedLabware, // Export force inclusion toggle state
    hasUnreferencedFixedLabware, // Export unreferenced fixed labware flag
    freeMode, // Expert free-placement mode flag
    freeModeDirty, // Whether manual edits were made since entering free mode
    initializeDeckLayout,
    verifyPosition,
    moveBetweenPositions,
    placeFromTray,
    unplaceToTray,
    resetDeckLayoutState,
  };
}
